/**
 * The fixed phase slots of a ripening program. Every program uses the same 7 slots in this
 * order; a slot is enabled, disabled (kept with its settings, can be switched on during a
 * cycle) or not used. This order is the current agreement and may change. Phase names live
 * only here: the ripening program service stays controller-neutral and reports phase
 * numbers, which AVOS names from this list.
 *
 * Stored in the program as it always was: an enabled phase is present (values.Enable absent
 * or 1), a disabled phase is present with values.Enable = 0, a slot that is not used is
 * absent from ripewise.phases.
 */

import type { RipewisePhase } from "../hooks/useRipeningPrograms";

export const PHASE_SLOT_NAMES = [
    "Egalisation",
    "Active gassing",
    "Passive gassing",
    "Ripening",
    "Extra gas",
    "Pre-cooling",
    "Cooling",
] as const;

export const PHASE_SLOT_COUNT = PHASE_SLOT_NAMES.length;

export const PHASE_SLOTS: number[] = PHASE_SLOT_NAMES.map((_, index) => index + 1);

export type PhaseState = "enabled" | "disabled" | "do_not_use";

export const PHASE_STATE_LABELS: Record<PhaseState, string> = {
    enabled: "Enabled",
    disabled: "Disabled",
    do_not_use: "Do not use",
};

// * A new program: extra gas is ready but off, every other phase is on.
export const DEFAULT_PHASE_STATES: Record<number, PhaseState> = {
    1: "enabled",
    2: "enabled",
    3: "enabled",
    4: "enabled",
    5: "disabled",
    6: "enabled",
    7: "enabled",
};

export const phaseSlotName = (slot: number): string => PHASE_SLOT_NAMES[slot - 1] ?? `Phase ${slot}`;

/**
 * Heading of a phase column or card: the phase name as title with "Phase N" below it.
 * A phase without a name is titled "Phase N" with nothing below.
 */
export const phaseHeading = (slot: number): { title: string; subtitle: string | null } => {
    const name = PHASE_SLOT_NAMES[slot - 1];
    return name ? { title: name, subtitle: `Phase ${slot}` } : { title: `Phase ${slot}`, subtitle: null };
};

/** "Extra gas (phase 5)": the name users know, with the slot number for reference. */
export const phaseSlotLabel = (slot: number): string => (
    PHASE_SLOT_NAMES[slot - 1] ? `${PHASE_SLOT_NAMES[slot - 1]} (phase ${slot})` : `Phase ${slot}`
);

/** An absent slot is not used; a present phase is disabled only with Enable = 0. */
export const isPhaseEnabled = (phase: Pick<RipewisePhase, "values"> | null | undefined): boolean => (
    !!phase && phase.values?.Enable !== 0
);

export function phaseStateOf(phase: Pick<RipewisePhase, "values"> | null | undefined): PhaseState {
    if (!phase) return "do_not_use";
    return isPhaseEnabled(phase) ? "enabled" : "disabled";
}
